import { use, useEffect } from 'react';
import type { DeferredRequestKey } from '~/constants/common/deferredRequests';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import { generateErrorLogger } from '~/utilities/loggers';
import { useDeferredStreamDebugLogger } from './useDeferredStreamDebugLogger';

type OwnProps<T> = {
  deferredKey: DeferredRequestKey;
  onResolve: (data: T | undefined) => void;
  promise: Promise<T | undefined>;
};

// Render inside a <Suspense> boundary; it suspends only itself while the value streams in.
export function DeferredResolution<T>({
  deferredKey,
  onResolve,
  promise
}: OwnProps<T>) {
  const data = use(promise);
  const eventCorrelationId = useEventCorrelationId();
  const logDebug = useDeferredStreamDebugLogger();

  useEffect(() => {
    if (data === undefined) {
      generateErrorLogger(deferredKey, { eventCorrelationId })(
        new Error('Deferred stream degraded on client')
      );
    }

    logDebug(deferredKey, data);
    onResolve(data);
  }, [data, deferredKey, eventCorrelationId, logDebug, onResolve]);

  return null;
}
